import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { adminService } from "@/services/adminService";
import { Button, PercentInput } from "@/components/ui";
import { Percent } from "lucide-react";
import {
  commissionPercentStringFromRate,
  formatCommissionPercentLabel,
  rateFromPercentInput,
} from "@/lib/commissionRate";
import type { Activity } from "@/types";
import toast from "react-hot-toast";
import { toastApiError } from "@/lib/errorMessages";
import { StaffConfigRow } from "./StaffConfigRow";

interface AdminActivityCommissionSectionProps {
  activity: Activity;
}

export function AdminActivityCommissionSection({
  activity,
}: AdminActivityCommissionSectionProps) {
  const { t } = useTranslation("activities");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const activityId = activity.id;

  const [editing, setEditing] = useState(false);
  const [percentDraft, setPercentDraft] = useState("");

  const commissionLabel = formatCommissionPercentLabel(
    activity.commission_rate,
  );

  const seedPercentStr = useMemo(
    () => commissionPercentStringFromRate(activity.commission_rate),
    [activity.commission_rate],
  );

  const updateConfigMutation = useMutation({
    mutationFn: (config: { commission_rate: number }) =>
      adminService.updateActivityConfig(activityId, config),
    onSuccess: async () => {
      toast.success(t("detail.adminCommissionToastSaved"));
      await queryClient.invalidateQueries({
        queryKey: ["activity", activityId],
      });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({
        queryKey: ["admin", "seller", activity.user_id, "activities"],
      });
      setEditing(false);
    },
    onError: (err) => {
      toastApiError(err, "updateActivity");
    },
  });

  return (
    <StaffConfigRow
      icon={Percent}
      title={t("detail.adminCommissionTitle")}
      hint={t("detail.adminCommissionHint")}
      actions={
        editing ? (
          <>
            <div className="flex items-center gap-1">
              <PercentInput
                value={percentDraft}
                onChange={(e) => setPercentDraft(e.target.value)}
                className="h-8 w-20 px-2"
                aria-label={t("detail.adminCommissionPercentAria")}
              />
              <span className="text-surface-500 text-xs">%</span>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                const rate = rateFromPercentInput(percentDraft);
                if (rate === null) {
                  toast.error(t("detail.adminCommissionPercentInvalid"));
                  return;
                }
                updateConfigMutation.mutate({ commission_rate: rate });
              }}
              isLoading={updateConfigMutation.isPending}
            >
              {tCommon("save")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setEditing(false)}
            >
              {tCommon("cancel")}
            </Button>
          </>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setPercentDraft(seedPercentStr);
              setEditing(true);
            }}
          >
            {t("detail.adminCommissionEdit")}
          </Button>
        )
      }
    >
      <span className="text-surface-900 text-sm font-semibold tabular-nums">
        {commissionLabel}
      </span>
    </StaffConfigRow>
  );
}
